import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Lightbulb, Bug, Heart, MessageSquare, Star, StarOff } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { v: "want", l: "Wants", icon: Lightbulb },
  { v: "issue", l: "Issues", icon: Bug },
  { v: "praise", l: "Praise", icon: Heart },
  { v: "general", l: "General", icon: MessageSquare },
];
const STATUSES = ["new", "reviewing", "done", "dismissed"];

export default function AdminFeedbackTab() {
  const [tab, setTab] = useState("want");
  const [items, setItems] = useState([]);

  const load = async () => {
    try { const r = await api.get("/admin/feedback"); setItems(r.data); } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const update = async (id, fields) => {
    try { await api.put(`/admin/feedback/${id}`, fields); load(); } catch { toast.error("Update failed"); }
  };

  const toggleTestimonial = async (f) => {
    const approving = !f.testimonial_approved;
    if (approving && !f.testimonial_allow_publish) {
      if (!window.confirm(`${f.user_name} didn't explicitly opt in to publishing. Approve anyway? (Only do this if you have separate permission.)`)) return;
    }
    try {
      await api.post(`/admin/feedback/${f.id}/testimonial`, { approve: approving });
      toast.success(approving ? "Published to homepage" : "Removed from homepage");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const filtered = items.filter((f) => f.category === tab);

  return (
    <div className="hc-card p-6" data-testid="admin-feedback-tab">
      <h3 className="font-display text-xl mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#3A5A4A]" /> Feedback inbox ({items.length})</h3>
      <div className="flex gap-1.5 flex-wrap mb-5">
        {TABS.map((t) => {
          const count = items.filter((f) => f.category === t.v).length;
          const active = tab === t.v;
          return (
            <button key={t.v} onClick={() => setTab(t.v)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition ${active ? "bg-[#3A5A4A] text-white" : "bg-[#F3EFEA] text-[#7A736E] hover:bg-[#EBE5DF]"}`} data-testid={`fb-tab-${t.v}`}>
              <t.icon className="w-4 h-4" /> {t.l} ({count})
            </button>
          );
        })}
      </div>
      <div className="space-y-3">
        {filtered.length === 0 && <p className="text-sm text-[#7A736E] text-center py-6">Nothing in this view.</p>}
        {filtered.map((f) => (
          <div key={f.id} className="border border-[#EBE5DF] rounded-xl p-4" data-testid={`fb-item-${f.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{f.user_name || f.user_email}</p>
                <p className="text-xs text-[#7A736E]">
                  {new Date(f.created_at).toLocaleDateString()} · {f.page || "?"}
                  {f.rating ? ` · ${"★".repeat(f.rating)}` : ""}
                </p>
              </div>
              <select value={f.status || "new"} onChange={(e) => update(f.id, { status: e.target.value })} className="text-xs rounded-full px-2 py-1 border border-[#EBE5DF]" data-testid={`fb-status-${f.id}`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <p className="text-sm whitespace-pre-wrap">{f.message}</p>
            <div className="mt-2 flex gap-2 flex-wrap text-xs items-center">
              {f.category === "praise" && (
                <button
                  onClick={() => toggleTestimonial(f)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full transition ${f.testimonial_approved ? "bg-[#3A5A4A] text-white hover:bg-[#2C4538]" : "bg-[#E8DCC8] text-[#3A5A4A] hover:bg-[#D8E2D8]"}`}
                  data-testid={`fb-testimonial-${f.id}`}
                  title={f.testimonial_approved ? "Hide from homepage" : "Publish on homepage"}
                >
                  {f.testimonial_approved
                    ? (<><StarOff className="w-3 h-3" /> Unpublish testimonial</>)
                    : (<><Star className="w-3 h-3" fill="currentColor" /> Publish on homepage</>)}
                </button>
              )}
              {f.source === "review_request" && (
                <span className="text-[10px] uppercase tracking-wider text-[#7A736E] px-1.5 py-0.5 rounded-full bg-[#F3EFEA]">
                  via review request
                </span>
              )}
              {TABS.filter((t) => t.v !== f.category).map((t) => (
                <button key={t.v} onClick={() => update(f.id, { category: t.v })} className="text-[#7A736E] hover:text-[#3A5A4A] underline" data-testid={`fb-recat-${f.id}-${t.v}`}>
                  → {t.l}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
